import { Injectable, OnDestroy, inject } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import {
	BehaviorSubject,
	Subscription,
	combineLatest,
	interval,
	lastValueFrom
} from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { decodeJwtPayload } from 'src/app/utilities/jwt/jwt.utility';
import { environment } from 'src/environments/environment';
import { LoggingService } from '../../logger/logging.service';

@Injectable({
	providedIn: 'root'
})
export class TokenRefreshService implements OnDestroy {
	private oidcSecurityService = inject(OidcSecurityService);
	private loggingService = inject(LoggingService);

	private readonly REFRESH_INTERVAL_MS = 60 * 1000; // Check every 60 seconds
	private readonly REFRESH_BUFFER_SECONDS = 120; // Refresh 2 minutes before expiry

	private refreshTimer: Subscription | null = null;
	private isMonitoring = new BehaviorSubject<boolean>(false);
	private lastRefreshAttempt = 0;
	private readonly MIN_REFRESH_INTERVAL = 30 * 1000; // Prevent rapid refresh attempts

	/**
	 * Starts proactive token monitoring and refresh
	 * Call this when the user is authenticated
	 */
	public startMonitoring(): void {
		if (this.isMonitoring.value) {
			this.stopMonitoring();
		}

		this.logTokenRefresh('Starting token monitoring');

		this.isMonitoring.next(true);
		void this.runRefreshCheck();

		// Create a timer that checks token status every 60 seconds
		this.refreshTimer = interval(this.REFRESH_INTERVAL_MS)
			.pipe(
				filter(() => this.isMonitoring.value),
				switchMap(() => this.checkAndRefreshToken()),
				catchError((error) => {
					this.loggingService.logException(
						new Error('Error in token refresh cycle')
					);
					this.logTokenRefresh(
						'Error in token refresh cycle: ' + error
					);
					return [];
				})
			)
			.subscribe();
	}

	/**
	 * Stops token monitoring
	 */
	public stopMonitoring(): void {
		this.logTokenRefresh('Stopping token monitoring');
		this.isMonitoring.next(false);

		if (this.refreshTimer) {
			this.refreshTimer.unsubscribe();
			this.refreshTimer = null;
		}
	}

	/**
	 * Forces an immediate token refresh if needed
	 */
	public async forceRefreshIfNeeded(): Promise<void> {
		const now = Date.now();

		// Prevent rapid refresh attempts
		if (now - this.lastRefreshAttempt < this.MIN_REFRESH_INTERVAL) {
			this.logTokenRefresh(
				'Skipping refresh - too soon since last attempt'
			);
			return;
		}

		this.lastRefreshAttempt = now;

		try {
			const authResult = await lastValueFrom(
				this.oidcSecurityService.checkAuth()
			);

			if (!authResult.isAuthenticated) {
				this.logTokenRefresh(
					'User not authenticated, stopping monitoring'
				);
				this.stopMonitoring();
				return;
			}

			const token = await lastValueFrom(
				this.oidcSecurityService.getAccessToken().pipe(take(1))
			);

			if (!token) {
				this.logTokenRefresh(
					'No access token available, forcing refresh'
				);
				await lastValueFrom(
					this.oidcSecurityService.forceRefreshSession()
				);
				this.logTokenRefresh('Missing token recovery completed');
				return;
			}

			const tokenExpirationTime = await this.getTokenExpirationTime();
			if (
				tokenExpirationTime &&
				this.shouldRefreshToken(tokenExpirationTime)
			) {
				this.logTokenRefresh('Forcing token refresh');

				await lastValueFrom(
					this.oidcSecurityService.forceRefreshSession()
				);

				this.logTokenRefresh('Token refresh completed');
			}
		} catch (error) {
			this.loggingService.logException(
				new Error('Error during forced refresh')
			);
			throw error;
		}
	}

	/**
	 * Manually trigger a token refresh regardless of expiration time
	 */
	public async manualRefresh(): Promise<boolean> {
		try {
			this.logTokenRefresh('Manual token refresh initiated');

			const refreshResult = await lastValueFrom(
				this.oidcSecurityService.forceRefreshSession()
			);

			if (refreshResult.isAuthenticated) {
				this.logTokenRefresh('Manual token refresh successful');
				return true;
			} else {
				this.logTokenRefresh(
					'Manual token refresh failed - user no longer authenticated'
				);
				this.stopMonitoring();
				return false;
			}
		} catch {
			this.loggingService.logException(
				new Error('Manual token refresh failed')
			);
			return false;
		}
	}

	ngOnDestroy(): void {
		this.stopMonitoring();
	}

	private checkAndRefreshToken() {
		return combineLatest([
			this.oidcSecurityService.isAuthenticated$,
			this.oidcSecurityService.getAccessToken()
		]).pipe(
			take(1),
			switchMap(async ([authResult, token]) => {
				if (!authResult.isAuthenticated) {
					this.logTokenRefresh('User not authenticated');
					this.stopMonitoring();
					return { action: 'not_authenticated' };
				}

				if (!token) {
					this.logTokenRefresh(
						'No access token available, attempting recovery'
					);
					await this.triggerTokenRefresh();
					return { action: 'token_recovered' };
				}

				const tokenExpirationTime = await this.getTokenExpirationTime();
				if (
					tokenExpirationTime &&
					this.shouldRefreshToken(tokenExpirationTime)
				) {
					this.logTokenRefresh(
						'Token expiring soon, triggering refresh'
					);
					await this.triggerTokenRefresh();
					return { action: 'token_refreshed' };
				} else if (tokenExpirationTime) {
					const timeUntilExpiry = Math.floor(
						(tokenExpirationTime - Date.now()) / 1000 / 60
					);
					this.logTokenRefresh(
						`Token valid for ${timeUntilExpiry} more minutes`
					);
					return { action: 'token_valid', timeUntilExpiry };
				}

				return { action: 'no_expiration_time' };
			})
		);
	}

	private shouldRefreshToken(expirationTime: number): boolean {
		const now = Date.now();
		const timeUntilExpiry = (expirationTime - now) / 1000; // Convert to seconds

		return timeUntilExpiry <= this.REFRESH_BUFFER_SECONDS;
	}

	private async getTokenExpirationTime(): Promise<number | null> {
		try {
			const token = await lastValueFrom(
				this.oidcSecurityService.getAccessToken()
			);
			if (!token) return null;

			const payload = decodeJwtPayload(token);
			const expiration = payload?.exp;

			return typeof expiration === 'number' &&
				Number.isFinite(expiration) &&
				expiration > 0
				? expiration * 1000
				: null;
		} catch {
			this.loggingService.logException(
				new Error('Error parsing token expiration')
			);
			return null;
		}
	}

	private async triggerTokenRefresh(): Promise<void> {
		try {
			// Use both silent renewal and forced refresh for maximum reliability
			const refreshResult = await lastValueFrom(
				this.oidcSecurityService.forceRefreshSession()
			);

			if (refreshResult.isAuthenticated) {
				this.logTokenRefresh('Token refresh successful');
			} else {
				this.logTokenRefresh(
					'Token refresh failed - user no longer authenticated'
				);
				this.stopMonitoring();
			}
		} catch {
			this.loggingService.logException(new Error('Token refresh failed'));

			// If refresh fails, try one more time with checkAuth
			try {
				await lastValueFrom(this.oidcSecurityService.checkAuth());
			} catch {
				this.logTokenRefresh('Secondary auth check also failed');
				this.stopMonitoring();
			}
		}
	}

	private async runRefreshCheck(): Promise<void> {
		try {
			await lastValueFrom(this.checkAndRefreshToken());
		} catch (error) {
			this.loggingService.logException(
				new Error('Error during initial token refresh check')
			);
			this.logTokenRefresh(
				'Initial token refresh check failed: ' + error
			);
		}
	}

	private logTokenRefresh(message: string): void {
		const loggers = environment.loggers as { tokenRefresh?: boolean };
		if (!loggers?.tokenRefresh) return;

		console.debug(`=====[TOKEN REFRESH] ${message}`);
	}
}
